import { Device } from '@capacitor/device';
import {
  AttachmentSource,
  ExportableDataType,
  Machine,
} from '../api/graphql/generated';
import { LockedAxies } from '../scratchpad/application/shared/component/flow-item/shift/flow-item-shift';
import { logger } from '../util/logger';
import { ApplicationConfig } from './config';
import { trackIntercomEvent } from './intercom';

export interface PlaylistAnalyticsOptions {
  fromShortcutMenu?: boolean;
}

declare global {
  interface Window {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    analytics: any;
  }
}

const configureAnalytics = (): void => {
  const analytics = (window.analytics = window.analytics || []);
  if (!analytics.initialize) {
    if (analytics.invoked) {
      logger.error('segment snippet included twice');
    } else {
      analytics.invoked = !0;
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'trackMultiEdit',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware',
      ];

      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      analytics.factory = function (e: any): any {
        return function (...args: any[]) {
          const t = Array.prototype.slice.call(args);
          t.unshift(e);
          analytics.push(t);
          return analytics;
        };
      };
      for (let e = 0; e < analytics.methods.length; e++) {
        const key = analytics.methods[e];
        analytics[key] = analytics.factory(key);
      }
      analytics.load = (
        writeKey: string | undefined,
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        options?: any
      ): void => {
        if (writeKey) {
          const t = document.createElement('script');
          t.type = 'text/javascript';
          t.async = !0;
          t.src = `https://cdn.segment.com/analytics.js/v1/${writeKey}/analytics.min.js`;
          const n = document.getElementsByTagName('script')[0];
          n.parentNode?.insertBefore(t, n);
          analytics._loadOptions = options;
        }
      };
      analytics.SNIPPET_VERSION = '4.13.1';
      analytics.load(ApplicationConfig.analytics.segmentKey);
    }
  }
};

const setAnalyticsUserContext = (
  tenantId: string,
  id = 'unknown',
  email: string
): void => {
  if (window.analytics) {
    window.analytics.identify(id, {
      email,
    });
    window.analytics.group(tenantId);
  }
};

const pageViewEvent = async (title?: string): Promise<void> => {
  if (window.analytics) {
    const device = await Device.getInfo();
    window.analytics.page(title, title, { device });
  }
};

interface TrackEventOptions {
  eventName: string;
  payload?: Record<string, unknown>;
  forwardToIntercom?: boolean;
}

const trackEvent = async (options: TrackEventOptions): Promise<void> => {
  const { eventName, payload, forwardToIntercom } = options;

  if (window.analytics) {
    const device = await Device.getInfo();
    window.analytics.track(eventName, { ...payload, device });
  }

  if (forwardToIntercom) {
    await trackIntercomEvent(eventName, payload);
  }
};

const trackCreateNewTenant = (): void => {
  trackEvent({
    eventName: 'Admin - Create new tenant',
  });
};

const trackRegisterMachine = (machine: Partial<Machine>): void => {
  if (machine.demo) {
    trackEvent({
      eventName: 'Onboarding - Register Demo Machine',
      payload: {
        isDemoMachine: machine.demo,
      },
    });
  } else {
    trackEvent({
      eventName: 'Onboarding - Register Machine',
      payload: {
        isDemoMachine: machine.demo,
      },
      forwardToIntercom: true,
    });
  }
};

const trackReactivatingMachine = (machine: Partial<Machine>): void => {
  if (machine.demo) {
    trackEvent({
      eventName: 'Machine - Reactivate Demo',
      payload: {
        isDemoMachine: machine.demo,
      },
    });
  } else {
    trackEvent({
      eventName: 'Machine - Reactivate',
      payload: {
        isDemoMachine: machine.demo,
      },
      forwardToIntercom: true,
    });
  }
};

const trackInitiateRegisterMachine = (): void => {
  trackEvent({
    eventName: 'Onboarding - Initiate Register Machine',
  });
};

const trackCreateChart = (): void => {
  trackEvent({
    eventName: 'Chart - Create',
  });
};

const trackInitiateCreateChart = (): void => {
  trackEvent({
    eventName: 'Chart - Initiate Create',
  });
};

const trackInitiateChartSuggestion = (): void => {
  trackEvent({
    eventName: 'Feedback - Initiate Chart Suggestion',
  });
};

const trackInvitedUser = (): void => {
  trackEvent({
    eventName: 'Onboarding - Invited User',
    forwardToIntercom: true,
  });
};

const trackCreatedTeam = (): void => {
  trackEvent({
    eventName: 'Onboarding - Created Team',
    forwardToIntercom: true,
  });
};

const trackMultiEdit = (withPartDefaultsUpdate = false): void => {
  trackEvent({
    eventName: 'Cobot Welder - Multi-edit',
    forwardToIntercom: true,
    payload: {
      withPartDefaultsUpdate,
    },
  });
};

const trackMoveToPoint = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Move to Point',
  });
};

const trackExternalLink = (name: string, url: string): void => {
  trackEvent({ eventName: name, payload: { url } });
};

const trackFavoriteVariable = (): void => {
  trackEvent({ eventName: 'Machine - Favorited Variable' });
};

const trackFavoriteIO = (): void => {
  trackEvent({
    eventName: 'Machine - Favorited IO',
  });
};

const trackSetTenantName = (): void => {
  trackEvent({
    eventName: 'Onboarding - Set Tenant Name',
    forwardToIntercom: true,
  });
};

const trackSetUsername = (): void => {
  trackEvent({
    eventName: 'Onboarding - Set User Name',
    forwardToIntercom: true,
  });
};

const trackDisabledUser = (): void => {
  trackEvent({
    eventName: 'Admin - Disabled User',
  });
};

const trackEnabledUser = (): void => {
  trackEvent({
    eventName: 'Admin - Enabled User',
  });
};

const trackAccessedTenantUser = (): void => {
  trackEvent({
    eventName: 'Admin - Viewed Tenant Users',
    forwardToIntercom: true,
  });
};

const trackRequestedTenantExport = (options: {
  dataTypes: Array<ExportableDataType>;
}): void => {
  trackEvent({
    eventName: 'Admin - Requested Tenant Export',
    payload: {
      ...options,
    },
  });
};

const trackNavigatedFromUserToTeam = (): void => {
  trackEvent({
    eventName: 'Admin - Navigated From User To Team',
  });
};

const trackMachineChangedTeam = (): void => {
  trackEvent({
    eventName: 'Machine - Changed Team',
  });
};

const trackMachineDeactivated = (): void => {
  trackEvent({
    eventName: 'Machine - Deactivated',
  });
};

const trackCreateChatMessage = (): void => {
  trackEvent({
    eventName: 'Chat - Create Message',
  });
};

const trackInitiateCreateChatMessage = (): void => {
  trackEvent({
    eventName: 'Chat - Initiate Create Message',
  });
};

const trackCreateChatReply = (): void => {
  trackEvent({
    eventName: 'Chat - Create Chat Reply',
  });
};

const trackCreateScratchpad = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - Create',
    payload: { slug },
  });
};

const trackUpdatedScratchpad = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - Update',
    payload: { slug },
  });
};

const trackRemoveScratchpad = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - Remove',
    payload: { slug },
  });
};

const trackRestoreScratchpad = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - Restore',
    payload: { slug },
  });
};

const trackViewScratchpadHistory = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - History Opened',
    payload: { slug },
  });
};

const trackScratchpadHistoryRestore = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - History Restore Version',
    payload: { slug },
  });
};

const trackScratchpadHistoryCompareVersions = (
  slug: string | null = null
): void => {
  trackEvent({
    eventName: 'Scratchpad - History Compare Versions',
    payload: { slug },
  });
};

const trackCopyScratchpad = (slug: string | null = null): void => {
  trackEvent({
    eventName: 'Scratchpad - Copy',
    payload: { slug },
  });
};

const trackCreatedPlaylist = (options?: PlaylistAnalyticsOptions): void => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Created',
    payload: {
      ...options,
    },
  });
};

const trackAddToPlaylist = (options?: PlaylistAnalyticsOptions): void => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Add Item',
    payload: {
      ...options,
    },
  });
};
const trackRemoveFromPlaylist = (): void => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Remove Item',
  });
};

const trackReorderedPlaylist = (): void => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Reorder',
  });
};

const trackPlaylistToggledAutoplay = ({
  autoPlayToNext,
}: {
  autoPlayToNext: boolean;
}): void => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Toggled Autoplay',
    payload: {
      autoPlayToNext,
    },
  });
};

const trackPlaylistShowSearch = () => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Show Search',
  });
};

const trackPlaylistLoad = () => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Load',
  });
};

const trackPlaylistUpdated = (canRestore = false) => {
  trackEvent({
    eventName: 'Scratchpad Playlist - Updated',
    payload: {
      canRestore,
    },
  });
};

const trackCobotWelderCopyFlowItems = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Copy Flow Items',
  });
};

const trackCobotWelderPasteFlowItems = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Paste Flow Items',
  });
};

export type TrackCobotWelderSelectionType =
  | 'other'
  | 'linear'
  | 'radial'
  | 'tack'
  | 'rotary'
  | 'all'
  | 'stitch';
interface TrackCobotWelderSelectionOptions {
  method: 'actionBar' | 'item';
  count: number;
  totalSelected: number;
  flowLength: number;
  type?: TrackCobotWelderSelectionType;
}

const trackCobotWelderSelectAllItems = (flowLength: number): void => {
  logger.info('tracking cobot welder select all items', { flowLength });
  trackEvent({
    eventName: 'Cobot Welder - Select All Items',
    payload: {
      flowLength,
    },
  });
};

const trackCobotWelderUnselectAllItems = (flowLength: number): void => {
  logger.info('tracking cobot welder unselect all', { flowLength });
  trackEvent({
    eventName: 'Cobot Welder - Unselect All Items',
    payload: {
      flowLength,
    },
  });
};

const trackCobotWelderSelectItems = (
  options: TrackCobotWelderSelectionOptions
): void => {
  logger.info('tracking cobot welder select items', { options });
  trackEvent({
    eventName: 'Cobot Welder - Select Items',
    payload: {
      ...options,
    },
  });
};

const trackCobotWelderUnselectItems = (
  options: TrackCobotWelderSelectionOptions
): void => {
  logger.info('tracking cobot welder unselect items', { options });
  trackEvent({
    eventName: 'Cobot Welder - Unselect Items',
    payload: {
      ...options,
    },
  });
};

const trackStartFromHere = (longPress?: boolean): void => {
  trackEvent({
    eventName: 'Cobot Welder - Start From Here',
    forwardToIntercom: !longPress,
    payload: {
      longPress,
    },
  });
};

const trackCobotWelderEnableStepMode = (source: 'startFromHere'): void => {
  trackEvent({
    eventName: 'Cobot Welder - Step Mode Enabled',
    payload: {
      source,
    },
  });
};

const trackCobotWelderLaunchedBucketExplorer = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Launched bucket explorer',
  });
};

const trackCobotWelderLaunchedBucketWriter = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Launched bucket writer',
  });
};

const trackCobotWelderAppliedBucketSettingsToPartSettings = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Applied bucket settings to part settings',
  });
};

const trackCobotWelderAppliedWeldSettingsToPart = (
  hasLibrary?: boolean
): void => {
  trackEvent({
    eventName: 'Cobot Welder - Applied weld settings to part',
    payload: {
      hasLibrary: hasLibrary ?? false,
    },
    forwardToIntercom: !hasLibrary,
  });
};

const trackCobotWelderAppliedWeldSettingsToPartAndUpdated = (): void => {
  trackEvent({
    eventName:
      'Cobot Welder - Applied weld settings to part and updated library',
  });
};

const trackCobotWelderAppliedWeldSettingsToPartAndCreated = (): void => {
  trackEvent({
    eventName:
      'Cobot Welder - Applied weld settings to part and created library',
  });
};

const trackCobotWelderHideLibraryNotes = (): void => {
  trackEvent({
    eventName: 'Cobot Welder - Hiding library notes',
  });
};

const trackScratchpadOptimisticLockAction = (forceSave = false): void => {
  trackEvent({
    eventName: 'Scratchpad - Optimistic Lock Action',
    payload: {
      forceSave,
    },
  });
};

interface TrackedNotificationProps extends Record<string, unknown> {
  source?: string;
}

const trackNavigatedFromNotification = (
  options?: TrackedNotificationProps
): void => {
  trackEvent({
    eventName: 'Notification - Navigation',
    payload: options,
  });
};

const trackSearchLaunched = (): void => {
  trackEvent({
    eventName: 'Search - Launched',
  });
};

const trackSearchResultClicked = (): void => {
  trackEvent({
    eventName: 'Search - Result Clicked',
  });
};

const trackChatNotificationSubscription = (subscribed: boolean): void => {
  trackEvent({
    eventName: 'Chat - Toggle chat notifications',
    payload: {
      subscribed,
    },
  });
};

const trackAttachmentUploadIntent = (
  sources: Partial<AttachmentSource>[]
): void => {
  trackEvent({
    eventName: 'Attachments - Upload Intent',
    payload: {
      sources,
    },
  });
};

const trackAttachmentUploaded = (): void => {
  trackEvent({
    eventName: 'Attachments - Uploaded',
  });
};

const trackLaunchedAttachmentPreview = (): void => {
  trackEvent({
    eventName: 'Attachments - Opened Preview',
  });
};

const trackLaunchedAttachmentViewer = (): void => {
  trackEvent({
    eventName: 'Attachments - Opened Viewer',
  });
};

const trackAttachmentDownloaded = (): void => {
  trackEvent({
    eventName: 'Attachments - Downloaded Attachment',
  });
};

const trackPlanPaywallPresented = (options: {
  planEntitlementKey: string;
  isBillingManager: boolean;
}): void => {
  trackEvent({
    eventName: 'Plan - Presented Paywall',
    payload: {
      ...options,
    },
  });
};

const trackPlanFeatureLearnMore = (options: {
  planEntitlementKey: string;
  isBillingManager: boolean;
}): void => {
  trackEvent({
    eventName: 'Plan - Feature Learn More',
    payload: {
      ...options,
    },
  });
};

const trackPlanFeqtureRequested = (options: {
  planEntitlementKey: string;
}): void => {
  trackEvent({
    eventName: 'Plan - Requested Feature',
    payload: {
      ...options,
    },
  });
};

/**
 * Indicates the user initiated an action to subscribe to a plan.
 * Since the transaction occurs in another system, at this point in the flow
 * we only know their intent but not whether they followed through.
 */
const trackSubscribePlanIntent = (options: {
  planId: string;
  priceId: string;
}): void => {
  trackEvent({
    eventName: 'Plan - Subscribe Plan Intent',
    payload: {
      ...options,
    },
    forwardToIntercom: true,
  });
};

/**
 * Indicates the user initiated an action to manage their current plan.
 * Since the transaction occurs in another system, at this point in the flow
 * we only know their intent but not whether they followed through.
 */
const trackManagePlanIntent = (options: { planId: string }): void => {
  trackEvent({
    eventName: 'Plan - Manage Plan Intent',
    payload: {
      ...options,
    },
  });
};

const trackCobotWelderPresentedUnsavedItems = (discarded = false) => {
  trackEvent({
    eventName: 'Cobot Welder - Presented Unsaved Items',
    payload: {
      discarded,
    },
  });
};

const trackCobotWelderResetPartSettings = () => {
  trackEvent({
    eventName: 'Cobot Welder - Reset Part Settings',
  });
};

const trackCobotWelderPartSettingsSaveAsDefault = (saveAsDefault = false) => {
  trackEvent({
    eventName: 'Cobot Welder - Part Settings Save As Default',
    payload: {
      saveAsDefault,
    },
  });
};

const trackCobotWelderSectionAdd = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Add',
  });
};

const trackCobotWelderSectionRemove = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Remove',
  });
};

const trackCobotWelderSectionRemoveAll = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Remove All',
  });
};

const trackCobotWelderSectionCopyAll = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Copy All',
  });
};

const trackCobotWelderSectionCollapse = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Collapse',
  });
};

const trackCobotWelderSectionExpand = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Expand',
  });
};

const trackCobotWelderSectionCollapseAll = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Collapse All',
  });
};

const trackCobotWelderSectionExpandAll = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Expand All',
  });
};

const trackCobotWelderSectionJumpTo = () => {
  trackEvent({
    eventName: 'Cobot Welder - Section Jump To',
  });
};

const trackCobotWelderReorderFlowItem = () => {
  trackEvent({
    eventName: 'Cobot Welder - ReorderFlowItem',
  });
};

const trackCobotWelderShiftInitiated = () => {
  trackEvent({
    eventName: 'Cobot Welder - Shift Initiation Action',
  });
};

const trackCobotWelderShiftExpandedAxisDetail = () => {
  trackEvent({
    eventName: 'Cobot Welder - Shift Expanded Axis Detail',
  });
};

const trackCobotWelderShiftLockedAxis = (options: {
  lockedAxis: LockedAxies;
}) => {
  trackEvent({
    eventName: 'Cobot Welder - Shift Locked Axis',
    payload: {
      ...options,
    },
  });
};

const trackCobotWelderShiftInPlace = (options: {
  lockedAxies: LockedAxies;
  numOfItems: number;
  isOriginInitialItem: boolean;
}) => {
  trackEvent({
    eventName: 'Cobot Welder - Shift In Place',
    payload: {
      ...options,
    },
  });
};

const trackCobotWelderShiftAndCopy = (options: {
  lockedAxies: LockedAxies;
  numOfItems: number;
  isOriginInitialItem: boolean;
}) => {
  trackEvent({
    eventName: 'Cobot Welder - Shift and Copy',
    payload: {
      ...options,
    },
  });
};

const trackCobotWelderShiftCapturedAirMove = () => {
  trackEvent({
    eventName: 'Cobot Welder - Shift Captured Air Move',
  });
};

const trackOpenSupport = (source: string) => {
  trackEvent({
    eventName: 'Support - Open',
    payload: {
      source,
    },
  });
};

const trackScratchpadRecommendationCreated = () => {
  trackEvent({
    eventName: 'Scratchpad Recommendation Created',
  });
};

const trackScratchpadRecommendationSaved = (utilized: boolean) => {
  trackEvent({
    eventName: 'Scratchpad Recommendation Saved',
    payload: {
      utilized,
    },
  });
};

const trackScratchpadSaveAttemptWhileInFlight = () => {
  trackEvent({
    eventName: 'Scratchpad Save Attempt While In-Flight',
  });
};

const trackMachineOfflineToastIgnore = () => {
  trackEvent({
    eventName: 'Machine - Ignore offline message',
  });
};

const trackFeatureTourStep = (key: string) => {
  trackEvent({
    eventName: 'Feature Tour - Step',
    payload: {
      key,
    },
  });
};

const trackCobotWelderEnhancedStitchNudge = () => {
  trackEvent({
    eventName: 'Cobot Welder - Enhanced Stitch Nudge',
  });
};

const trackCustomRoleCreated = (options: { roleType: string }) => {
  trackEvent({
    eventName: 'Custom Role - Created',
    payload: {
      ...options,
    },
  });
};

const trackCustomRoleDeleted = (options: { roleType: string }) => {
  trackEvent({
    eventName: 'Custom Role - Deleted',
    payload: {
      ...options,
    },
  });
};

export {
  configureAnalytics,
  pageViewEvent,
  setAnalyticsUserContext,
  trackAccessedTenantUser,
  trackAddToPlaylist,
  trackAttachmentDownloaded,
  trackAttachmentUploadIntent,
  trackAttachmentUploaded,
  trackChatNotificationSubscription,
  trackCobotWelderAppliedBucketSettingsToPartSettings,
  trackCobotWelderAppliedWeldSettingsToPart,
  trackCobotWelderAppliedWeldSettingsToPartAndCreated,
  trackCobotWelderAppliedWeldSettingsToPartAndUpdated,
  trackCobotWelderCopyFlowItems,
  trackCobotWelderEnableStepMode,
  trackCobotWelderEnhancedStitchNudge,
  trackCobotWelderHideLibraryNotes,
  trackCobotWelderLaunchedBucketExplorer,
  trackCobotWelderLaunchedBucketWriter,
  trackCobotWelderPartSettingsSaveAsDefault,
  trackCobotWelderPasteFlowItems,
  trackCobotWelderPresentedUnsavedItems,
  trackCobotWelderReorderFlowItem,
  trackCobotWelderResetPartSettings,
  trackCobotWelderSectionAdd,
  trackCobotWelderSectionCollapse,
  trackCobotWelderSectionCollapseAll,
  trackCobotWelderSectionCopyAll,
  trackCobotWelderSectionExpand,
  trackCobotWelderSectionExpandAll,
  trackCobotWelderSectionJumpTo,
  trackCobotWelderSectionRemove,
  trackCobotWelderSectionRemoveAll,
  trackCobotWelderSelectAllItems,
  trackCobotWelderSelectItems,
  trackCobotWelderShiftAndCopy,
  trackCobotWelderShiftCapturedAirMove,
  trackCobotWelderShiftExpandedAxisDetail,
  trackCobotWelderShiftInPlace,
  trackCobotWelderShiftInitiated,
  trackCobotWelderShiftLockedAxis,
  trackCobotWelderUnselectAllItems,
  trackCobotWelderUnselectItems,
  trackCreateChart,
  trackCreateChatMessage,
  trackCreateChatReply,
  trackCreateNewTenant,
  trackCreateScratchpad,
  trackCopyScratchpad,
  trackCreatedPlaylist,
  trackCreatedTeam,
  trackCustomRoleCreated,
  trackCustomRoleDeleted,
  trackDisabledUser,
  trackEnabledUser,
  trackEvent,
  trackExternalLink,
  trackFavoriteIO,
  trackFavoriteVariable,
  trackInitiateChartSuggestion,
  trackInitiateCreateChart,
  trackInitiateCreateChatMessage,
  trackInitiateRegisterMachine,
  trackInvitedUser,
  trackLaunchedAttachmentPreview,
  trackLaunchedAttachmentViewer,
  trackMachineChangedTeam,
  trackMachineDeactivated,
  trackManagePlanIntent,
  trackMoveToPoint,
  trackMultiEdit,
  trackNavigatedFromNotification,
  trackNavigatedFromUserToTeam,
  trackOpenSupport,
  trackPlanFeatureLearnMore,
  trackPlanFeqtureRequested,
  trackPlanPaywallPresented,
  trackPlaylistLoad,
  trackPlaylistShowSearch,
  trackPlaylistToggledAutoplay,
  trackPlaylistUpdated,
  trackReactivatingMachine,
  trackRegisterMachine,
  trackRemoveFromPlaylist,
  trackRemoveScratchpad,
  trackReorderedPlaylist,
  trackRequestedTenantExport,
  trackRestoreScratchpad,
  trackScratchpadHistoryCompareVersions,
  trackScratchpadHistoryRestore,
  trackScratchpadOptimisticLockAction,
  trackScratchpadRecommendationCreated,
  trackScratchpadRecommendationSaved,
  trackScratchpadSaveAttemptWhileInFlight,
  trackSearchLaunched,
  trackSearchResultClicked,
  trackSetTenantName,
  trackSetUsername,
  trackStartFromHere,
  trackSubscribePlanIntent,
  trackUpdatedScratchpad,
  trackViewScratchpadHistory,
  trackMachineOfflineToastIgnore,
  trackFeatureTourStep,
};
